<template>
    <v-dialog :value="value" @input="$emit('input', $event)" max-width="680px" persistent scrollable aria-label="Project settings dialog">
        <v-card class="quaroshadow" style="">
            <v-card-title class="d-flex align-center primary white--text">
                <v-icon class="mr-2" color="white">mdi-cog</v-icon>
                <div>
                    <div class="text-h6">Project Settings</div>
                    <div class="text-caption white--text">Update project title, brand name and domain</div>
                </div>
                <v-spacer />
                <v-btn icon color="white" @click="close" :disabled="saving" aria-label="Close dialog">
                    <v-icon color="white">mdi-close</v-icon>
                </v-btn>
            </v-card-title>

            <v-divider />

            <v-card-text class="mt-5">
                <v-form ref="form">
                    <v-row dense>
                        <v-col cols="12">
                            <v-text-field v-model="form.title" label="Project title" rounded filled dense :disabled="saving" hide-details="auto" required />
                        </v-col>
                        <v-col cols="12" md="6">
                            <v-text-field class="mt-1" v-model="form.brandName" label="Brand name" rounded filled dense :disabled="saving" hide-details="auto" required />
                        </v-col>
                        <v-col cols="12" md="6">
                            <v-text-field
                                class="mt-1"
                                v-model="form.brandDomain"
                                label="Brand domain"
                                placeholder="example.com"
                                prepend-inner-icon="mdi-web"
                                rounded
                                filled
                                dense
                                :disabled="saving"
                                hide-details="auto"
                                hint="Without http(s) and www., only the domain"
                                persistent-hint />
                        </v-col>
                    </v-row>

                    <v-divider class="my-4" />

                    <!-- Platforms -->
                    <v-row dense>
                        <v-col cols="12">
                            <div class="text-subtitle-2 mb-2">Platforms</div>

                            <!-- Aktuell aktivierte Plattformen -->
                            <div class="mb-3">
                                <v-chip
                                    v-for="key in selectedPlatforms"
                                    :key="key"
                                    class="mr-2 mb-2"
                                    small
                                    :disabled="saving || removePlatformDialog.loading"
                                    :close="canRemovePlatform(key)"
                                    close-icon="mdi-close-circle-outline"
                                    :aria-label="'Remove platform ' + platformLabel(key)"
                                    @click:close="onRemovePlatformClick(key)">
                                    <span
                                        v-if="platformLogo(key)"
                                        v-html="platformLogo(key)"
                                        style="height: 16px; width: 16px; border-radius: 3px; margin-right: 6px; display: inline-block; vertical-align: middle" />
                                    {{ platformLabel(key) }}
                                </v-chip>
                                <span v-if="!selectedPlatforms.length" class="grey--text text--darken-1">No platforms enabled yet.</span>
                                <div v-if="selectedPlatforms.some((key) => isPersistedPlatform(key) && canRemovePlatform(key))" class="text-caption grey--text text--darken-1">
                                    Removing a platform deletes its tracked responses and stops its updates. Its credits are released after the current update cycle.
                                </div>
                                <div v-if="project.paused" class="text-caption grey--text text--darken-1">
                                    <v-icon x-small class="mr-1">mdi-pause-circle-outline</v-icon>
                                    This project is paused. Added platforms are imported and charged once you resume the project.
                                </div>
                            </div>

                            <!-- Neue Plattform hinzufügen -->

                            <v-autocomplete
                                v-model="addPlatformModel"
                                :items="availableToAdd"
                                item-text="name"
                                item-value="key"
                                :return-object="false"
                                label="Add platform"
                                dense
                                rounded
                                filled
                                clearable
                                :disabled="saving || project.demo_project"
                                hide-details="auto"
                                @input="onAddPlatformChange">
                                <template v-slot:item="{ item, on, attrs }">
                                    <v-list-item v-on="on" v-bind="attrs" :disabled="item.disabled === true">
                                        <span v-if="item.logo" v-html="item.logo" style="height: 18px; width: 18px; margin-right: 10px; display: inline-block; vertical-align: middle" />
                                        <v-list-item-title>
                                            {{ item.name }}
                                            <v-chip class="ml-2" v-if="item.disabled === true" x-small>not available on selected location</v-chip>
                                        </v-list-item-title>
                                    </v-list-item>
                                </template>
                            </v-autocomplete>
                        </v-col>
                    </v-row>

                    <v-divider class="my-4" />

                    <!-- Update-Intervall -->
                    <v-row dense>
                        <v-col cols="12">
                            <div class="text-subtitle-2 mb-1">Update Interval</div>
                            <v-chip-group class="py-0" color="primary" column v-model="intervalIndex">
                                <v-chip v-for="(option, i) in intervalOptions" :key="i" small filter filter-icon="mdi-check-circle" :disabled="saving">
                                    <span style="text-transform: capitalize"> {{ option.text }}</span>
                                </v-chip>
                            </v-chip-group>
                            <!-- Vorschau über Intervall UND neue Plattformen - dieselbe Zahl wie im Bestätigungsdialog -->
                            <div v-if="needsCreditConfirm && !project.paused && !project.demo_project" class="mt-1 text-caption">
                                <v-icon x-small class="mr-1">mdi-database-outline</v-icon>
                                Credit usage changes from <b>{{ usageBefore.total }}</b> to <b>{{ usageAfterSave.total }}</b> {{ usageAfterSave.unit }} per month.
                            </div>
                            <div v-if="needsCreditConfirm && project.paused" class="mt-1 text-caption">
                                <v-icon x-small class="mr-1">mdi-pause-circle-outline</v-icon>
                                This project is paused. Credits for these changes are reserved when you resume it.
                            </div>
                        </v-col>
                    </v-row>
                </v-form>
            </v-card-text>
            <DeleteDialog
                :deleteDialog="removePlatformDialog.show"
                :selectedDeleteItem="platformLabel(removePlatformDialog.key)"
                deleteLabel="Platform"
                :loading="removePlatformDialog.loading"
                @delete="removePersistedPlatform"
                @close="removePlatformDialog.loading || (removePlatformDialog.show = false)" />
            <CreditChangeConfirmDialog
                v-model="creditConfirmDialog"
                :project="project"
                title="Confirm Credit Usage"
                :changes="creditChangeLines"
                :before="usageBefore.total"
                :after="usageAfterSave.total"
                :after-interval="usageAfterInterval.total"
                :unit="usageAfterSave.unit"
                :release-date="releaseDate"
                :loading="saving"
                @confirm="onConfirmCreditChange" />

            <v-divider />

            <v-card-actions>
                <v-spacer />
                <v-btn text @click="close" :disabled="saving">Cancel</v-btn>
                <v-btn color="primary" :loading="saving" :disabled="!canSave" @click="onSave">
                    <v-icon left small>mdi-content-save</v-icon>
                    Save
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
    import DeleteDialog from "../dialogs/DeleteDialog.vue"
    import { isAmazonRufusLocation } from "../../utils/platformMeta"
    import CreditChangeConfirmDialog from "../dialogs/CreditChangeConfirmDialog.vue"
    import {
        INTERVAL_OPTIONS,
        intervalOptionIndex,
        intervalLabel,
        projectCreditUsage,
        currentCycleEnd,
        deleteProjectPlatform,
        importInProgressMessage,
        fetchEntryCount,
        withEntryCount
    } from "../helper/projectStatus"

    // Zusätzliche Events gegenüber "save":
    //   platform-removed(key, result)   -> Plattform wurde serverseitig gelöscht, Projekt lokal anpassen
    //   notify(message, icon, color)    -> Benachrichtigung in der Elternansicht anzeigen
    // Das "save"-Payload enthält zusätzlich `interval` (neuer Wert oder null, wenn unverändert).
    export default {
        name: "ProjectSettingsDialog",
        components: { DeleteDialog, CreditChangeConfirmDialog },
        props: {
            value: { type: Boolean, default: false },
            project: { type: Object, required: true },
            saving: { type: Boolean, default: false },
            // Alle verfügbaren Plattformen: [{ key, name, logo?, disabled? }]
            platformsAll: { type: Array, default: () => [] },
            // Aktuell aktivierte Plattformen (Keys)
            platformsEnabled: { type: Array, default: () => [] }
        },
        data() {
            return {
                form: { title: "", brandName: "", brandDomain: "" },
                selectedPlatforms: [],
                addPlatformModel: null,
                intervalOptions: INTERVAL_OPTIONS,
                intervalIndex: intervalOptionIndex(this.project && this.project.interval),
                removePlatformDialog: { show: false, key: "", loading: false },
                creditConfirmDialog: false,
                // aktuelle Prompt-Zahl vom Server, beim Öffnen geladen (null bis dahin)
                entryCount: null
            }
        },
        computed: {
            // nur eine echte Änderung zählt: "monthly" bei einem Altprojekt mit 30 Tagen bleibt unverändert
            selectedInterval() {
                const option = this.intervalOptions[this.intervalIndex]
                return option ? option.value : null
            },
            intervalChanged() {
                return this.selectedInterval !== null && this.intervalIndex !== intervalOptionIndex(this.project && this.project.interval)
            },
            // Projekt mit aktueller Prompt-Zahl: nach einem Prompt-Upload kennt die offene Ansicht die neuen noch nicht
            usageProject() {
                return withEntryCount(this.project, this.entryCount)
            },
            usageBefore() {
                return projectCreditUsage(this.usageProject)
            },
            usageAfter() {
                return projectCreditUsage(this.usageProject, this.selectedInterval)
            },
            // bisherige Plattformen mit dem Intervall nach dem Speichern: trennt im Bestätigungsdialog den
            // Intervall-Anteil (Verkürzung wird erst zum Zyklusende frei) von den sofort reservierten neuen Plattformen
            usageAfterInterval() {
                return projectCreditUsage(this.usageProject, this.intervalChanged ? this.selectedInterval : this.project && this.project.interval)
            },
            // Frisch ausgewählte, noch nicht gespeicherte Plattformen - kosten beim Speichern Credits
            addedPlatformKeys() {
                return this.selectedPlatforms.filter((key) => !this.isPersistedPlatform(key))
            },
            // Verbrauch nach dem Speichern: neues Intervall UND neue Plattformen. Die Eltern speichern erst das
            // Intervall, danach bucht /project/add/platforms/later die Plattformen bereits zum neuen Intervall.
            usageAfterSave() {
                const p = this.usageProject || {}
                const added = this.addedPlatformKeys
                const platforms = (Array.isArray(p.platforms) ? p.platforms : []).concat(added.filter((key) => key !== "google"))
                const google = !!p.google || added.includes("google")
                return projectCreditUsage({ ...p, platforms, google }, this.intervalChanged ? this.selectedInterval : p.interval)
            },
            // Bestätigung nötig, sobald sich die Reservierung ändern würde
            needsCreditConfirm() {
                return this.intervalChanged || this.addedPlatformKeys.length > 0
            },
            creditChangeLines() {
                const lines = []
                if (this.intervalChanged) lines.push("Update interval: " + intervalLabel(this.project.interval) + " to " + intervalLabel(this.selectedInterval))
                if (this.addedPlatformKeys.length) lines.push("New platforms: " + this.addedPlatformKeys.map((key) => this.platformLabel(key)).join(", "))
                return lines
            },
            // Ende der laufenden Periode unter dem bisherigen Intervall: dann wird eine alte Reservierung frei
            releaseDate() {
                return currentCycleEnd(this.project, this.project && this.project.interval)
            },
            canSave() {
                if (this.intervalChanged && this.form.title && this.form.brandName) return true
                if (!this.form.title || !this.form.brandName) return false
                const currTitle = this.project?.title || ""
                const currBrandName = this.project?.brand?.brand_name || ""
                const currBrandDomain = this.normalizeDomain(this.project?.brand?.brand_domain || "")
                const titleChanged = this.form.title !== currTitle
                const nameChanged = this.form.brandName !== currBrandName
                const domainChanged = this.normalizeDomain(this.form.brandDomain) !== currBrandDomain
                const currentEnabled = Array.isArray(this.platformsEnabled) ? this.platformsEnabled.slice().sort() : []
                const selected = this.selectedPlatforms.slice().sort()
                const platformsChanged = JSON.stringify(currentEnabled) !== JSON.stringify(selected)
                return titleChanged || nameChanged || domainChanged || platformsChanged
            },
            availableToAdd() {
                const enabled = new Set(this.selectedPlatforms)
                const blockGoogle = !!(this.project && this.project.google)
                const aiModeBlocked = this.project?.langloc?.google_ai_mode_enabled === false
                const rufusBlocked = !isAmazonRufusLocation(this.project?.langloc)
                return (this.platformsAll || [])
                    .filter((p) => p && p.key && p.key !== "all" && !enabled.has(p.key) && !(blockGoogle && p.key === "google") && p.key !== "openai" && p.key !== "claude")
                    .map((p) => ({
                        key: p.key,
                        name: p.name || p.key,
                        logo: p.logo,
                        disabled: (aiModeBlocked && (p.key === "google_ai_mode" || p.key === "google")) || (rufusBlocked && p.key === "amazon_rufus")
                    }))
                    .sort((a, b) => Number(a.disabled) - Number(b.disabled))
            }
        },
        watch: {
            value: {
                immediate: true,
                handler(val) {
                    if (!val) return
                    this.fillFromProject()
                    this.loadEntryCount()
                }
            },
            project: {
                handler() {
                    this.fillFromProject()
                },
                deep: true,
                immediate: true
            },
            platformsEnabled: {
                handler() {
                    this.selectedPlatforms = Array.isArray(this.platformsEnabled) ? [...this.platformsEnabled] : []
                    // Sicherstellen, dass "google" enthalten ist, wenn project.google aktiv ist
                    if (this.project && this.project.google && !this.selectedPlatforms.includes("google")) {
                        this.selectedPlatforms.push("google")
                    }
                },
                immediate: true
            }
        },
        mounted() {
            this.fillFromProject()
        },
        methods: {
            async loadEntryCount() {
                if (!this.project || !this.project._id) return
                this.entryCount = null
                this.entryCount = await fetchEntryCount({ projectId: this.project._id, axiosPost: this.$helpers.axiosPost.bind(this.$helpers), cryptoJS: this.CryptoJS })
            },
            fillFromProject() {
                const p = this.project || {}
                const domain = this.normalizeDomain(p?.brand?.brand_domain || "")
                this.form = {
                    title: p?.title || "",
                    brandName: p?.brand?.brand_name || "",
                    brandDomain: domain
                }
                this.selectedPlatforms = Array.isArray(this.platformsEnabled) ? [...this.platformsEnabled] : []
                // "google" automatisch hinzufügen, wenn das Projekt google-gebunden ist
                if (p.google && !this.selectedPlatforms.includes("google")) {
                    this.selectedPlatforms.push("google")
                }
                this.intervalIndex = intervalOptionIndex(p.interval)
            },
            // Ist die Plattform am Projekt gespeichert (und nicht nur hier frisch ausgewählt)?
            isPersistedPlatform(key) {
                if (key === "google") return !!(this.project && this.project.google)
                return Array.isArray(this.project && this.project.platforms) && this.project.platforms.includes(key)
            },
            // Frisch ausgewählte Plattformen lassen sich immer wieder abwählen. Gespeicherte nur, wenn
            // danach noch mindestens eine Plattform in `platforms` übrig bleibt - ein Projekt ohne
            // Plattform hat keine Updates mehr, und eine leere Liste behandelt die Projektansicht als
            // "alle Plattformen".
            canRemovePlatform(key) {
                if (this.saving || (this.project && this.project.demo_project)) return false
                if (!this.isPersistedPlatform(key)) return true
                const persistedCount = Array.isArray(this.project.platforms) ? this.project.platforms.length : 0
                return key === "google" ? persistedCount >= 1 : persistedCount >= 2
            },
            onRemovePlatformClick(key) {
                if (!this.isPersistedPlatform(key)) {
                    this.removePlatform(key)
                    return
                }
                this.removePlatformDialog = { show: true, key, loading: false }
            },
            async removePersistedPlatform() {
                const key = this.removePlatformDialog.key
                this.removePlatformDialog.loading = true
                try {
                    const result = await deleteProjectPlatform({
                        projectId: this.project._id,
                        platform: key,
                        axiosPost: this.$helpers.axiosPost.bind(this.$helpers),
                        cryptoJS: this.CryptoJS
                    })
                    if (result.error) {
                        const message =
                            result.error === "platform_adding_in_progress"
                                ? "This platform is currently being added. Please try again later."
                                : result.error === "import_in_progress"
                                ? importInProgressMessage(result.import_type)
                                : "Something went wrong while removing the platform. Please try again or contact the support!"
                        this.$emit("notify", message, "mdi-alert-circle-outline", "error")
                        return
                    }
                    this.removePlatform(key)
                    this.$emit("platform-removed", key, result)
                    const released = Number(result.released_credits) || 0
                    this.$emit(
                        "notify",
                        "Removed platform: " + this.platformLabel(key) + "." + (released > 0 ? " " + released + " prompt updates per month will be released after the current update cycle." : ""),
                        "mdi-check-circle",
                        "primary"
                    )
                } finally {
                    this.removePlatformDialog = { show: false, key: "", loading: false }
                }
            },
            normalizeDomain(v) {
                return (v || "")
                    .toString()
                    .trim()
                    .replace(/^https?:\/\//i, "")
                    .replace(/^www\./i, "")
                    .split("/")[0]
            },
            platformLabel(key) {
                const it = (this.platformsAll || []).find((p) => p.key === key)
                return it?.name || key
            },
            platformLogo(key) {
                const it = (this.platformsAll || []).find((p) => p.key === key)
                return it?.logo || null
            },
            addPlatform(key) {
                if (!key) return
                if (!this.selectedPlatforms.includes(key)) this.selectedPlatforms.push(key)
            },
            removePlatform(key) {
                const i = this.selectedPlatforms.indexOf(key)
                if (i > -1) this.selectedPlatforms.splice(i, 1)
            },
            close() {
                if (this.saving) return
                this.$emit("input", false)
                this.$emit("close")
            },
            onSave() {
                // Intervall oder neue Plattformen verändern die Credit-Reservierung -> vorher bestätigen lassen
                if (this.needsCreditConfirm) {
                    this.creditConfirmDialog = true
                    return
                }
                this.emitSave()
            },
            onConfirmCreditChange() {
                this.creditConfirmDialog = false
                this.emitSave()
            },
            emitSave() {
                const payload = {
                    title: this.form.title,
                    brandName: this.form.brandName,
                    brandDomain: this.normalizeDomain(this.form.brandDomain),
                    platforms: this.selectedPlatforms.slice(),
                    interval: this.intervalChanged ? this.selectedInterval : null
                }
                console.log("Saving project settings with payload:", payload)
                this.$emit("save", payload)
            },
            onAddPlatformChange(key) {
                const k = key && typeof key === "object" ? key.key : key
                this.addPlatform(k)
                this.addPlatformModel = null
            }
        }
    }
</script>
